import { useEffect, useRef } from 'react';
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import { cn } from '../lib/cn';

export interface OtpInputProps {
  /** Anzahl der Stellen. Standard 6. */
  laenge?: number;
  wert: string;
  onWert: (wert: string) => void;
  /** Wird ausgelöst, sobald alle Stellen gefüllt sind (Auto-Verify). */
  onVollstaendig?: (code: string) => void;
  fehler?: boolean;
  gesperrt?: boolean;
  autoFokus?: boolean;
  ariaLabel?: string;
  /** Id des Elements mit der Fehlermeldung. */
  fehlerId?: string;
}

const NUR_ZIFFERN = /\D+/g;

/**
 * Eingabe eines Anmeldecodes.
 *
 * - Automatische Weiterschaltung zum nächsten Feld, Rücksprung bei Rücktaste.
 * - Einfügen eines vollständigen Codes füllt alle Felder.
 * - Auto-Verify, sobald alle Stellen belegt sind.
 * - Fehlerzustand mit dezentem Schütteln; bei reduzierter Bewegung entfällt es.
 */
export function OtpInput({
  laenge = 6,
  wert,
  onWert,
  onVollstaendig,
  fehler = false,
  gesperrt = false,
  autoFokus = false,
  ariaLabel = 'Anmeldecode',
  fehlerId,
}: OtpInputProps) {
  const felder = useRef<Array<HTMLInputElement | null>>([]);
  const steuerung = useAnimationControls();
  const reduziert = useReducedMotion();
  const zuletztGemeldet = useRef<string>('');

  useEffect(() => {
    if (autoFokus) felder.current[0]?.focus();
  }, [autoFokus]);

  useEffect(() => {
    if (!fehler) return;
    if (reduziert) return;
    void steuerung.start({
      x: [0, -7, 7, -5, 5, 0],
      transition: { duration: 0.36, ease: 'easeInOut' },
    });
  }, [fehler, reduziert, steuerung]);

  function uebernehmen(neu: string) {
    const bereinigt = neu.replace(NUR_ZIFFERN, '').slice(0, laenge);
    onWert(bereinigt);
    if (bereinigt.length === laenge && zuletztGemeldet.current !== bereinigt) {
      zuletztGemeldet.current = bereinigt;
      onVollstaendig?.(bereinigt);
    }
    if (bereinigt.length < laenge) {
      zuletztGemeldet.current = '';
    }
  }

  function fokussiere(index: number) {
    const ziel = felder.current[Math.max(0, Math.min(laenge - 1, index))];
    ziel?.focus();
    ziel?.select();
  }

  function beiAenderung(e: ChangeEvent<HTMLInputElement>, index: number) {
    const eingabe = e.target.value.replace(NUR_ZIFFERN, '');
    if (eingabe === '') return;

    // Mehrere Ziffern (Autofill des SMS-Codes) ab der aktuellen Stelle einsetzen.
    if (eingabe.length > 1) {
      const zusammen = (wert.slice(0, index) + eingabe).slice(0, laenge);
      uebernehmen(zusammen);
      fokussiere(zusammen.length);
      return;
    }

    const stellen = wert.padEnd(laenge, ' ').split('');
    stellen[index] = eingabe;
    uebernehmen(stellen.join('').replace(/ /g, ''));
    if (index < laenge - 1) fokussiere(index + 1);
  }

  function beiTaste(e: KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const stellen = wert.split('');
      if (stellen[index]) {
        stellen[index] = '';
        uebernehmen(stellen.join(''));
      } else if (index > 0) {
        stellen[index - 1] = '';
        uebernehmen(stellen.join(''));
        fokussiere(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      fokussiere(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      fokussiere(index + 1);
    }
  }

  function beiEinfuegen(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const eingefuegt = e.clipboardData.getData('text').replace(NUR_ZIFFERN, '').slice(0, laenge);
    if (!eingefuegt) return;
    uebernehmen(eingefuegt);
    fokussiere(eingefuegt.length);
  }

  return (
    <motion.div
      animate={steuerung}
      role="group"
      aria-label={ariaLabel}
      aria-invalid={fehler || undefined}
      aria-describedby={fehler ? fehlerId : undefined}
      className="flex items-center justify-center gap-2 sm:gap-3"
    >
      {Array.from({ length: laenge }, (_, index) => {
        const zeichen = wert[index] ?? '';
        return (
          <input
            key={index}
            ref={(el) => {
              felder.current[index] = el;
            }}
            value={zeichen}
            onChange={(e) => beiAenderung(e, index)}
            onKeyDown={(e) => beiTaste(e, index)}
            onPaste={beiEinfuegen}
            onFocus={(e) => e.currentTarget.select()}
            disabled={gesperrt}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            enterKeyHint="done"
            maxLength={laenge}
            aria-label={`Stelle ${index + 1} von ${laenge}`}
            className={cn(
              'h-14 w-11 rounded-lg border bg-surface text-center text-xl font-semibold tabellenziffern text-fg sm:h-16 sm:w-12',
              'transition-colors duration-fast ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
              'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-subtle',
              fehler
                ? 'border-danger'
                : zeichen
                  ? 'border-accent'
                  : 'border-border-strong hover:border-fg-subtle',
            )}
          />
        );
      })}
    </motion.div>
  );
}
